import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BsFillPatchQuestionFill } from 'react-icons/bs';

import Modal from '../../../components/modal';
import Input from '../../../components/input';
import Button from '../../../components/button';
import SearchableSelect from '../../../components/searchableSelect';
import Spinner from '../../../components/spinner';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';

import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { listVpnServices } from '../../../hooks/local/userReducer';
import { showSuccessMessage } from '../../../hooks/constants';
import { useRouterList, useSubnetList } from '../layout/resuableEffect';

const VpnServices = () => {
  document.title = 'VPN Services - VPN';

  const projectId = useSelector((state) => state.user.selectedProject?.id);
  const dispatch = useDispatch();
  const vpnServices = useSelector((state) => state.user.vpnServices);

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: '', name: '' });

  const routerList = useRouterList(projectId);
  const subnetList = useSubnetList(projectId, '');

  const routerOptions = useMemo(() => {
    const arr = Array.isArray(routerList) ? routerList : [];
    return [
      { value: '', label: 'Select router' },
      ...arr.map((r) => ({ value: r.id, label: r.name?.trim() ? r.name : r.id })),
    ];
  }, [routerList]);



  const refetch = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      await dispatch(listVpnServices(projectId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const filtered = useMemo(() => {
    const arr = Array.isArray(vpnServices) ? vpnServices : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [vpnServices, searchText]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filtered.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filtered.length / entriesPerPage) || 1;

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const createForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      router_id: '',
      admin_state_up: true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      router_id: Yup.string().required('Router is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!projectId) return;
      setLoading(true);
      try {
        await apiEndPoints.createVpnService(projectId, values);
        showSuccessMessage('VPN Service created');
        setCreateOpen(false);
        resetForm();
        await refetch();
      } finally {
        setLoading(false);
      }
    },
  });

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selected?.name || '',
      description: selected?.description || '',
      router_id: selected?.router_id || '',
      admin_state_up: selected?.admin_state_up ?? true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      router_id: Yup.string().required('Router is required'),
    }),
    onSubmit: async (values) => {
      if (!projectId || !selected?.id) return;
      setLoading(true);
      try {
        await apiEndPoints.updateVpnService(projectId, selected.id, values);
        showSuccessMessage('VPN Service updated');
        setEditOpen(false);
        setSelected(null);
        await refetch();
      } finally {
        setLoading(false);
      }
    },
  });

  const runDelete = async () => {
    if (!projectId || !confirmDelete.id) return;
    setLoading(true);
    try {
      await apiEndPoints.deleteVpnService(projectId, confirmDelete.id);
      showSuccessMessage('VPN Service deleted');
      setConfirmDelete({ open: false, id: '', name: '' });
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const routerName = (routerId) => {
    const r = (Array.isArray(routerList) ? routerList : []).find((x) => x.id === routerId);
    return r?.name || routerId || '—';
  };

  return (
    <div className="px-4 md:px-10 py-6 min-h-screen">
      <Spinner loading={loading} />

      <p className="crimson-font text-xl font-medium text-primaryBlue mb-6">
        VPN Services
      </p>

      <div className="grid md:flex md:items-center md:justify-between gap-4 mb-4">
        <div className="w-full md:w-1/2 lg:w-1/3">
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search VPN service..."
            className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
          />
        </div>
        <div
          onClick={() => setCreateOpen(true)}
          className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer w-fit"
        >
          Create VPN service
        </div>
      </div>

      <div className="overflow-x-auto rounded-md dark:text-white">
        <table className="w-full text-sm text-left overflow-hidden">
          <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
            <tr>
              <th className="px-6 py-4 whitespace-nowrap">Name</th>
              <th className="px-6 py-4 whitespace-nowrap">Router</th>
              <th className="px-6 py-4 whitespace-nowrap">Status</th>
              <th className="px-6 py-4 whitespace-nowrap">Admin State</th>
              <th className="px-6 py-4 whitespace-nowrap w-56"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/50 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
            {(Array.isArray(currentEntries) ? currentEntries : []).map((row) => (
              <tr
                key={row.id || row.uuid || row.name}
                className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
              >
                <td className="text-start py-4 px-6 text-primaryBlue">{row.name || '—'}</td>
                <td className="text-start py-4 px-6">{routerName(row.router_id)}</td>
                <td className="text-start py-4 px-6">{row.status || '—'}</td>
                <td className="text-start py-4 px-6">
                  {row.admin_state_up ? 'Up' : 'Down'}
                </td>
                <td className="text-start py-4 px-6 flex items-center gap-2">
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer"
                    onClick={() => {
                      setSelected(row);
                      setEditOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() =>
                      setConfirmDelete({
                        open: true,
                        id: row.id || row.uuid,
                        name: row.name || row.id || row.uuid,
                      })
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!filtered || filtered.length === 0) && (
              <tr>
                <td className="px-6 py-6 text-sm opacity-70" colSpan={5}>
                  No VPN services found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === 1 ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-sm text-primaryBlue dark:text-white">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === totalPages ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      <Modal
        title="Create VPN Service"
        isVisible={createOpen}
        onClose={() => setCreateOpen(false)}
        size="md:w-[45%]"
      >
        <form className="grid gap-4" onSubmit={createForm.handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={createForm.values.name}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            onError={createForm.touched.name ? createForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the VPN service."
          />
          <Input
            label="Description"
            name="description"
            value={createForm.values.description}
            onChange={createForm.handleChange}
            isLabelIcon={true}
            iconTitle="A description for the VPN service."
          />
          <SearchableSelect
            label="Router *"
            name="router_id"
            value={createForm.values.router_id}
            onChange={createForm.handleChange}
            options={routerOptions}
            onError={createForm.touched.router_id ? createForm.errors.router_id : null}
            isLabelIcon={true}
            iconTitle="The router and admin state fields require to be enabled."
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="admin_state_up"
              checked={!!createForm.values.admin_state_up}
              onChange={createForm.handleChange}
            />
            <span className="text-sm flex items-center">
              Admin State Up
              <BsFillPatchQuestionFill
                className="text-sm text-primaryBlue ml-1 cursor-pointer"
                title="Leave the admin state checked"
              />
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <Modal
        title="Edit VPN Service"
        isVisible={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        size="md:w-[45%]"
      >
        <form className="grid gap-4" onSubmit={editForm.handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={editForm.values.name}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.name ? editForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the VPN service."
          />
          <Input
            label="Description"
            name="description"
            value={editForm.values.description}
            onChange={editForm.handleChange}
            isLabelIcon={true}
            iconTitle="A description for the VPN service."
          />
          <SearchableSelect
            label="Router *"
            name="router_id"
            value={editForm.values.router_id}
            onChange={editForm.handleChange}
            options={routerOptions}
            onError={editForm.touched.router_id ? editForm.errors.router_id : null}
            isLabelIcon={true}
            iconTitle="The router and admin state fields require to be enabled."
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="admin_state_up"
              checked={!!editForm.values.admin_state_up}
              onChange={editForm.handleChange}
            />
            <span className="text-sm flex items-center">
              Admin State Up
              <BsFillPatchQuestionFill
                className="text-sm text-primaryBlue ml-1 cursor-pointer"
                title="Leave the admin state checked"
              />
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <Button buttonText="Update" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        name={confirmDelete.name}
        title="Delete VPN Service"
        description="Type the name exactly to confirm deletion"
        onClose={() => setConfirmDelete({ open: false, id: '', name: '' })}
        onConfirm={runDelete}
        isDeleting={loading}
      />
    </div>
  );
};

export default VpnServices;


